import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Settings as SettingsIcon, Save, Loader2, Bell, Shield, Palette } from 'lucide-react';

export default function SettingsPage({ activeBrand }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    citationAlerts: true,
    weeklyDigest: true,
    competitorAlerts: false,
    actionUpdates: true,
  });

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => setSaving(false), 1000);
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Account</span>
        <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Settings</h1>
      </div>

      {/* Profile */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" /> Profile
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Name</label>
            <input defaultValue={user?.name || ''} className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Email</label>
            <input defaultValue={user?.email || ''} disabled className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-stone-50 dark:bg-stone-900 text-stone-500" />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5" /> Notifications
        </h2>
        <div className="space-y-4">
          {[
            { key: 'citationAlerts', label: 'Citation Alerts', desc: 'Get notified when your brand is cited in a new LLM' },
            { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Receive a weekly summary of your visibility changes' },
            { key: 'competitorAlerts', label: 'Competitor Alerts', desc: 'Get notified when competitors gain or lose positions' },
            { key: 'actionUpdates', label: 'Action Updates', desc: 'Get notified when automated actions complete' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-stone-900 dark:text-white">{item.label}</p>
                <p className="text-sm text-stone-500">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                className={`w-11 h-6 rounded-full transition-colors ${notifications[item.key] ? 'bg-emerald-500' : 'bg-stone-300 dark:bg-stone-600'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${notifications[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Brand Settings */}
      {activeBrand && (
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
          <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5" /> Brand: {activeBrand.name}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Brand Name</label>
              <input defaultValue={activeBrand.name} className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Domain</label>
              <input defaultValue={activeBrand.domain} className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Category</label>
              <input defaultValue={activeBrand.category} className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Description</label>
              <input defaultValue={activeBrand.description} className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" />
            </div>
          </div>
        </div>
      )}

      <button onClick={handleSave} className="px-6 py-2.5 bg-stone-900 text-white dark:bg-white dark:text-stone-900 font-medium flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}
